import React, { useState } from 'react';
import { Box, Typography, Button, AppBar, Toolbar, Grid, keyframes } from '@mui/material';
import bookCoverImage from '../../../data/medbook/cover.png';

const sections = [
  { title: 'Treatments History', content: 'Details of past treatments.' },
  { title: 'Medical Purchases History', content: 'Details of medical purchases.' },
  { title: 'Check-Up Visit History', content: 'Records of check-up visits.' },
  { title: 'Consultations History', content: 'Records of consultations.' },
  { title: 'Sessions History', content: 'Details of therapy sessions.' },
  { title: 'Refill History', content: 'History of medication refills.' }
];

// Define the "book opening" and "book closing" animation keyframes
const openBookAnimation = keyframes`
  0% { transform: scale(1) rotateY(0deg); opacity: 1; }
  50% { transform: scale(1.1) rotateY(90deg); opacity: 0.8; }
  100% { transform: scale(1) rotateY(180deg); opacity: 0; }
`;

const closeBookAnimation = keyframes`
  0% { transform: scale(1) rotateY(180deg); opacity: 0; }
  50% { transform: scale(1.1) rotateY(90deg); opacity: 0.8; }
  100% { transform: scale(1) rotateY(0deg); opacity: 1; }
`;

const UniversalMedicalBook = () => {
  const [showContent, setShowContent] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState(['cover']);

  const handleCoverClick = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setShowContent(true);
      setIsAnimating(false);
      setNavigationHistory(['cover', 'tableOfContents']);
    }, 1000);
  };

  const handleSectionClick = (section) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSection(section);
      setNavigationHistory((prev) => [...prev, section.title]);
      setIsAnimating(false);
    }, 1000);
  };

  const handleBackClick = () => {
    const updatedHistory = [...navigationHistory];
    updatedHistory.pop();

    const lastView = updatedHistory[updatedHistory.length - 1];
    if (lastView === 'cover') {
      setShowContent(false);
    } else if (lastView === 'tableOfContents') {
      setCurrentSection(null);
    } else {
      const section = sections.find((sec) => sec.title === lastView);
      setCurrentSection(section);
    }
    setNavigationHistory(updatedHistory);
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  };

  const handleNextClick = () => {
    const currentIndex = sections.findIndex(sec => sec.title === currentSection.title);
    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      handleSectionClick(nextSection);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#121212', color: 'white', pt: { xs: '4rem', sm: '4.5rem', md: '5rem' }, pb: '3rem' }}>
      <AppBar position='static' sx={{ backgroundColor: '#333' }}>
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Typography variant='h6' sx={{ color: '#fff' }}>My Universal Medical Records</Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: { xs: '2rem', sm: '4.5rem', md: '5rem' } }}>
        {!showContent
          ? (
            <Box
              onClick={handleCoverClick}
              sx={{
                backgroundImage: `url(${bookCoverImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 2,
                cursor: 'pointer',
                width: { xs: '90%', sm: '70%', md: '20%', lg: '20%' },
                maxHeight: '100vh',
                height: { xs: '70vh', sm: '70vh', md: '60vh', lg: '60vh' },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)'
                },
                animation: isAnimating ? `${openBookAnimation} 1s forwards` : 'none'
              }}
            >
              <Typography variant='h4' sx={{ color: '#FFD700', fontSize: '1.4rem', position: 'absolute', top: '10%', zIndex: 1 }}>
                My Universal Medical Book
              </Typography>
              <Typography variant='h6' sx={{ color: '#fff', position: 'absolute', bottom: '27%', zIndex: 1 }}>
                Click to Open
              </Typography>
            </Box>
            )
          : (
            <Box sx={{ width: { xs: '90%', sm: '70%', md: '20%', lg: '20%' }, maxHeight: '100vh', height: { xs: '70vh', sm: '70vh', md: '60vh', lg: '60vh' }, border: '1px solid #FFD700', borderRadius: 2, p: 2, textAlign: 'center', position: 'relative', animation: isAnimating ? `${closeBookAnimation} 1s forwards` : 'none' }}>
              {!currentSection
                ? (
                  <>
                    <Typography variant='h5' sx={{ color: '#00e676', mb: 2 }}>Table of Contents</Typography>
                    <Grid container spacing={2} justifyContent='center'>
                      {sections.map((section, index) => (
                        <Grid item xs={12} sm={12} md={12} key={index}>
                          <Button
                  variant='outlined'
                  sx={{ width: '100%', backgroundColor: '#1f1f1f', color: '#fff', '&:hover': { backgroundColor: '#00e676', color: '#121212' } }}
                  onClick={() => handleSectionClick(section)}
                >
                  {section.title}
                </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                  )
                : (
                  <Box>
                    <Typography variant='h5' sx={{ color: '#00e676' }}>{currentSection.title}</Typography>
                    <Typography variant='body1' sx={{ color: '#fff', mt: 2 }}>{currentSection.content}</Typography>
                  </Box>
                  )}
              <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, display: 'flex', justifyContent: 'space-between' }}>
                {navigationHistory.length > 1 && (
                  <Button variant='text' sx={{ color: '#FFD700' }} onClick={handleBackClick}>
                    Back
                  </Button>
                )}
                {currentSection && (
                  <Button variant='text' sx={{ color: '#FFD700' }} onClick={handleNextClick} disabled={currentSection.title === sections[sections.length - 1].title}>
                    Next
                  </Button>
                )}
              </Box>
            </Box>
            )}
      </Box>
    </Box>
  );
};

export default UniversalMedicalBook;
